<template>
    <div>
        <div v-if="loading" class="py-6 text-center text-muted text-sm">{{ $t('general.loading') }}</div>
        <div v-else-if="visibleSlots.length === 0" class="py-6 text-center text-muted text-sm">{{ $t('wizard.no_slots') }}</div>
        <div v-else class="grid gap-2" :class="dense ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-3 sm:grid-cols-4'">
            <UTooltip v-for="s in visibleSlots" :key="s.start" :text="s.available ? '' : slotReasonLabel(s.reason)"
                      :disabled="s.available">
                <button
                    type="button"
                    :disabled="!s.available"
                    class="flex w-full flex-col items-center justify-center rounded-md border px-2 py-1.5 text-xs font-medium transition-colors"
                    :class="slotClass(s)"
                    @click="s.available && $emit('select', s)"
                >
                    {{ formatTime(s.start) }}
                    <span v-if="overnightUntil(s)"
                          class="mt-0.5 flex items-center gap-1 text-[10px] font-normal leading-none">
                        <UIcon name="i-lucide-moon" class="size-3 shrink-0" />
                        {{ $t('wizard.overnight_short', { time: overnightUntil(s) }) }}
                    </span>
                </button>
            </UTooltip>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import dayjs, { TZ, formatTime } from '@/libs/dayjs'
    import type { ISlot } from '@/types/Slot'

    const { t } = useI18n()

    const props = withDefaults(defineProps<{
        slots:    ISlot[]
        loading?: boolean
        // The currently picked span; slots it covers render as selected. With only a start
        // picked (multi-day, end still open) just that slot highlights.
        selectedStart?: string
        selectedEnd?:   string
        // Denser column count for the side-by-side multi-day layout.
        dense?: boolean
    }>(), {
        loading: false,
        selectedStart: '',
        selectedEnd: '',
        dense: false,
    })

    defineEmits<(e: 'select', slot: ISlot) => void>()

    const slotReasonLabel = (reason: string) => t(`wizard.slot_reason.${reason}`)

    // Closed hours are never bookable, so they are not offered at all (v1.15); occupied,
    // downtime and over-quota slots stay visible (greyed out, with the reason on hover).
    const visibleSlots = computed(() => props.slots.filter((s) => !s.closed))

    // Overnight slot: the grid's final slot holds the machine until the next day's first
    // slot — only when the night has no closed hours (v1.15). Absent on every other slot.
    const overnightUntil = (s: ISlot): string =>
        s.overnight && s.overnight_until ? dayjs(s.overnight_until).tz(TZ).format('HH:mm') : ''

    const isSelected = (s: ISlot): boolean => {
        if (!props.selectedStart) return false
        if (!props.selectedEnd) return s.start === props.selectedStart
        const from = dayjs(props.selectedStart).valueOf()
        const to = dayjs(props.selectedEnd).valueOf()
        return dayjs(s.start).valueOf() >= from && dayjs(s.end).valueOf() <= to
    }

    // Every available slot shares the same background; slots that consume quota (cost > 0)
    // carry a thin primary left edge as the only marker.
    const slotClass = (s: ISlot): string => {
        if (!s.available) return 'border-default bg-muted text-dimmed cursor-not-allowed'
        if (isSelected(s)) return 'border-primary bg-primary text-inverted'
        if (s.cost > 0) return 'border-default border-l-2 border-l-primary hover:border-primary text-default'
        return 'border-default hover:border-primary text-default'
    }
</script>
